import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { FormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import { ManageRackService } from 'src/app/core/services/manage-rack.service';
import { ProductService } from 'src/app/core/services/product.service';
import { SpinnerService } from 'src/app/core/services/spinner.service';
import { ToasterService } from 'src/app/core/services/toastr.service';
import { TranslateService } from '@ngx-translate/core';
import { Zone, Level, Shelve } from 'src/app/models/rack-management';
import { Subject, takeUntil } from 'rxjs';
import { PrintBarcodeModalComponent } from '../print-barcode-modal/print-barcode-modal.component';

interface ShelfWithQuantity extends Shelve {
  allocatedQuantity?: number;
  zoneName?: string;
  levelName?: string;
  zoneAbbreviation?: string;
  isSelected?: boolean;
  itemCount?: number;
}

interface ShelfSelectionData {
  productId: string;
  productName: string;
  requestedQuantity: number;
  vendorName: string;
  variantId?: string;
  suggestedShelves?: {quantity: number, shelfCode: string}[];
}

@Component({
  selector: 'mega-store-factory-shelf-selection',
  templateUrl: './shelf-selection.component.html',
  styleUrls: ['./shelf-selection.component.scss']
})
export class ShelfSelectionComponent implements OnInit {
  private destroy$ = new Subject<boolean>();
  
  filterForm: FormGroup;
  zones: Zone[] = [];
  levels: Level[] = [];
  shelves: ShelfWithQuantity[] = [];
  filteredShelves: ShelfWithQuantity[] = [];
  selectedShelves: ShelfWithQuantity[] = [];
  suggestedShelves: {quantity: number, shelfCode: string}[] = [];
  filteredSuggestedShelves: ShelfWithQuantity[] = [];
  
  totalRequestedQuantity: number;
  totalAllocatedQuantity = 0;
  remainingQuantity = 0;
  
  loading = false;
  
  constructor(
    public dialogRef: MatDialogRef<ShelfSelectionComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ShelfSelectionData,
    private fb: FormBuilder,
    private manageRackService: ManageRackService,
    private productService: ProductService,
    private spinnerService: SpinnerService,
    private toasterService: ToasterService,
    private translateService: TranslateService,
    private dialog: MatDialog
  ) {
    this.totalRequestedQuantity = data.requestedQuantity;
    this.remainingQuantity = data.requestedQuantity;
    
    this.filterForm = this.fb.group({
      zone: [''],
      level: [''],
      searchTerm: ['']
    });
    
    // Watch for filter changes
    this.filterForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.applyFilters();
    });
  }
  
  ngOnInit(): void {
    this.loadZones();
    if (this.data.suggestedShelves) {
      this.suggestedShelves = this.data.suggestedShelves;
    } else {
      this.shelveSuggestion();
    }
  }

  shelveSuggestion(): void {
    this.manageRackService.httpGet(`/Factory/shelveSuggest?inventory=${this.data.productId}`, false).pipe(takeUntil(this.destroy$)).subscribe((response: any) => {
      console.log(response);
      this.suggestedShelves = response || [];
      // Update filtered suggested shelves after getting suggestions
      if (this.shelves.length > 0) {
        this.applyFilters();
      }
    });
  }
  
  loadZones(): void {
    this.loading = true;
    // Load shelves first, then extract zones and levels from the nested data
    this.loadAllShelves();
  }
  
  loadAllShelves(): void {
    // Load all shelves without filtering by levelId
    this.manageRackService.httpGet('FactoryShelve')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const shelves = response || [];
          
          // Extract unique zones and levels from the nested shelf data
          const uniqueZones = new Map<string, Zone>();
          const uniqueLevels = new Map<string, Level>();
          
          this.shelves = shelves.map((shelf: any) => {
            // Extract zone and level information from the nested structure
            const zone = shelf.level?.zone;
            const level = shelf.level;
            
            // Add to unique collections
            if (zone && zone._id) {
              uniqueZones.set(zone._id, zone);
            }
            if (level && level._id) {
              uniqueLevels.set(level._id, level);
            }
            
            const shelfWithDetails: ShelfWithQuantity = {
              ...shelf,
              allocatedQuantity: 0,
              zoneName: zone?.name || 'Unknown Zone',
              levelName: level?.level || 'Unknown Level',
              zoneAbbreviation: zone?.abbreviation || zone?.name || 'Unknown',
              isSelected: false,
              itemCount: shelf.items?.length || 0,
              totalQuantity: shelf.items?.reduce((sum: number, item: { quantity: number }) => sum + item.quantity, 0) || 0
            };
            return shelfWithDetails;
          });
          // Update zones and levels arrays
          this.zones = Array.from(uniqueZones.values());
          this.levels = Array.from(uniqueLevels.values());
          
          this.filteredShelves = [...this.shelves];
          this.applyFilters(); // Apply filters to populate suggested shelves
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading shelves:', error);
          this.loading = false;
        }
      });
  }
  
  trackByShelfId(index: number, shelf: ShelfWithQuantity): string {
    return shelf._id || index.toString();
  }
  
  applyFilters(): void {
    const filters = this.filterForm.value;
    
    const filterShelf = (shelf: ShelfWithQuantity) => {
      let matches = true;
      
      // Zone filter
      if (filters.zone && filters.zone !== '') {
        // Compare with the zone ID from the shelf's nested structure
        const shelfZoneId = (shelf as any).level?.zone?._id;
        if (shelfZoneId !== filters.zone) {
          matches = false;
        }
      }
      
      // Level filter
      if (filters.level && filters.level !== '') {
        // Compare with the level ID from the shelf's nested structure
        const shelfLevelId = (shelf as any).level?._id;
        if (shelfLevelId !== filters.level) {
          matches = false;
        }
      }
      
      // Search term filter
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        const searchMatches = 
          shelf.shelve?.toLowerCase().includes(searchLower) ||
          shelf.zoneName?.toLowerCase().includes(searchLower) ||
          shelf.levelName?.toLowerCase().includes(searchLower) ||
          shelf.zoneAbbreviation?.toLowerCase().includes(searchLower);
        if (!searchMatches) {
          matches = false;
        }
      }
      
      return matches;
    };
    
    // Filter all shelves
    this.filteredShelves = this.shelves.filter(shelf => 
      !this.isSuggestedShelf(shelf) && filterShelf(shelf)
    );
    
    // Filter suggested shelves
    this.filteredSuggestedShelves = this.shelves.filter(shelf => 
      this.isSuggestedShelf(shelf) && filterShelf(shelf)
    );
  }
  
  onShelfSelect(shelf: ShelfWithQuantity): void {
    if (shelf.isSelected) {
      // Deselect shelf
      shelf.isSelected = false;
      shelf.allocatedQuantity = 0;
      this.selectedShelves = this.selectedShelves.filter(s => s._id !== shelf._id);
    } else {
      // Select shelf with minimum quantity of 1
      shelf.isSelected = true;
      shelf.allocatedQuantity = 1;
      this.selectedShelves.push(shelf);
    }
    
    this.updateQuantityCalculations();
  }
  
  onQuantityChange(shelf: ShelfWithQuantity, quantity: number): void {
    const maxAllowed = this.remainingQuantity + (shelf.allocatedQuantity || 0);
    
    // Allow setting values temporarily for validation display
    if (quantity < 1) {
      shelf.allocatedQuantity = 1;
    } else {
      shelf.allocatedQuantity = quantity;
    }
    
    if (shelf.allocatedQuantity === 0 && shelf.isSelected) {
      shelf.isSelected = false;
      this.selectedShelves = this.selectedShelves.filter(s => s._id !== shelf._id);
    } else if (shelf.allocatedQuantity > 0 && !shelf.isSelected) {
      shelf.isSelected = true;
      this.selectedShelves.push(shelf);
    }
    
    this.updateQuantityCalculations();
  }

  onQuantityKeyDown(event: KeyboardEvent, shelf: ShelfWithQuantity): void {
    // Allow: backspace, delete, tab, escape, enter and .
    if ([46, 8, 9, 27, 13, 110, 190].indexOf(event.keyCode) !== -1 ||
      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (event.keyCode === 65 && event.ctrlKey === true) ||
      (event.keyCode === 67 && event.ctrlKey === true) ||
      (event.keyCode === 86 && event.ctrlKey === true) ||
      (event.keyCode === 88 && event.ctrlKey === true) ||
      // Allow: home, end, left, right
      (event.keyCode >= 35 && event.keyCode <= 39)) {
      return;
    }
    // Ensure that it is a number and stop the keypress
    if ((event.shiftKey || (event.keyCode < 48 || event.keyCode > 57)) && (event.keyCode < 96 || event.keyCode > 105)) {
      event.preventDefault();
    }
  }

  onQuantityBlur(shelf: ShelfWithQuantity, value: string): void {
    const quantity = parseInt(value, 10);
    const maxAllowed = this.remainingQuantity + (shelf.allocatedQuantity || 0);
    
    if (isNaN(quantity) || quantity < 1) {
      shelf.allocatedQuantity = 1;
    } else if (quantity > maxAllowed) {
      shelf.allocatedQuantity = maxAllowed;
    } else {
      shelf.allocatedQuantity = quantity;
    }
    
    this.updateQuantityCalculations();
  }

  increaseQuantity(shelf: ShelfWithQuantity): void {
    const currentQuantity = shelf.allocatedQuantity || 0;
    const maxAllowed = this.remainingQuantity + currentQuantity;
    
    if (currentQuantity < maxAllowed) {
      this.onQuantityChange(shelf, currentQuantity + 1);
    }
  }

  decreaseQuantity(shelf: ShelfWithQuantity): void {
    const currentQuantity = shelf.allocatedQuantity || 0;
    
    if (currentQuantity > 1) {
      this.onQuantityChange(shelf, currentQuantity - 1);
    }
  }
  
  updateQuantityCalculations(): void {
    this.totalAllocatedQuantity = this.selectedShelves.reduce((sum, shelf) => sum + (shelf.allocatedQuantity || 0), 0);
    this.remainingQuantity = this.totalRequestedQuantity - this.totalAllocatedQuantity;
  }
  
  isValidAllocation(): boolean {
    return this.totalAllocatedQuantity === this.totalRequestedQuantity && this.selectedShelves.length > 0;
  }
  
  isQuantityExceeded(shelf: ShelfWithQuantity): boolean {
    return (shelf.allocatedQuantity || 0) > (this.remainingQuantity + (shelf.allocatedQuantity || 0));
  }

  isSuggestedShelf(shelf: ShelfWithQuantity): boolean {
    return this.suggestedShelves.some(suggested => suggested.shelfCode.toLowerCase().split('-')[2] === shelf.shelve.toLowerCase());
  }

  getSuggestedQuantity(shelf: ShelfWithQuantity): number {
    const suggestion = this.suggestedShelves.find(suggested => suggested.shelfCode.toLowerCase().split('-')[2] === shelf.shelve.toLowerCase());
    return suggestion ? suggestion.quantity : 0;
  }

  openPrintBarcodeModal(shelf: ShelfWithQuantity): void {
    // Create modal dialog for printing barcode
    const dialogRef = this.dialog.open(PrintBarcodeModalComponent, {
      width: '500px',
      maxWidth: '90vw',
      data: {
        shelf: shelf,
        productName: this.data.productName,
        variantId: this.data.variantId
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.print) {
        this.printBarcode(result.serialNumber, result.variantId);
      }
      
      // // Refresh shelf suggestions if quantities were updated
      // if (result && result.quantitiesUpdated) {
      //   this.shelveSuggestion();
      // }
    });
  }

  printBarcode(serialNumber: string, variantId: string): void {
    this.spinnerService.addToLoader('printBarcode');
    
    const printData = {
      serialNumber: serialNumber,
      variantId: variantId
    };

    this.productService.printShelfBarcode(printData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.spinnerService.removeFromLoader('printBarcode');
          this.toasterService.successToastr(
            this.translateService.instant('BarcodePrintedSuccessfully')
          );
        },
        error: () => {
          this.spinnerService.removeFromLoader('printBarcode');
        }
      });
  }

  onSubmit(): void {
    if (this.isValidAllocation()) {
      const result = {
        productId: this.data.productId,
        shelves: this.selectedShelves.map(shelf => ({
          shelfId: shelf._id,
          quantity: shelf.allocatedQuantity,
          shelfName: shelf.shelve,
          zoneName: shelf.zoneName,
          levelName: shelf.levelName
        })),
        totalAllocated: this.totalAllocatedQuantity
      };
      
      this.dialogRef.close(result);
    }
  }
  
  onCancel(): void {
    this.dialogRef.close();
  }
  
  clearFilters(): void {
    this.filterForm.reset();
    this.applyFilters();
  }

  getFilteredLevels(): Level[] {
    const selectedZoneId = this.filterForm.get('zone')?.value;
    if (!selectedZoneId) {
      return this.levels;
    }
    
    // Get unique levels from shelves that belong to the selected zone
    const uniqueLevels = new Map<string, Level>();
    
    this.shelves.forEach(shelf => {
      const shelfData = shelf as any;
      if (shelfData.level?.zone?._id === selectedZoneId) {
        const level = shelfData.level;
        if (level && level._id) {
          uniqueLevels.set(level._id, level);
        }
      }
    });
    
    return Array.from(uniqueLevels.values());
  }

  onZoneChange(): void {
    // Reset level selection when zone changes
    this.filterForm.patchValue({ level: '' });
    this.applyFilters();
  }
  
  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
} 